import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import Stack from '@mui/material/Stack';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';

import { Iconify } from 'src/components/iconify';

// ----------------------------------------------------------------------

export interface EntitySourceInfo {
  source: string;
  sourceId: string | null;
  syncedAt: string | null;
  isSynced: boolean;
}

export function getEntitySourceInfo(row: any): EntitySourceInfo {
  if (!row) {
    return { source: 'manual', sourceId: null, syncedAt: null, isSynced: false };
  }

  const rawSource =
    row?.customData?._source ||
    row?._source ||
    (typeof row?.source === 'string' && !row?.parties ? row.source : null);

  const rawSourceId = row?.customData?._sourceId || row?._sourceId || null;
  const rawSyncedAt = row?.customData?._syncedAt || row?._syncedAt || null;

  if (
    rawSource &&
    typeof rawSource === 'string' &&
    rawSource.toLowerCase() !== 'manual' &&
    rawSource.toLowerCase() !== 'system'
  ) {
    return {
      source: rawSource.toLowerCase(),
      sourceId: rawSourceId ? String(rawSourceId) : null,
      syncedAt: rawSyncedAt ? String(rawSyncedAt) : null,
      isSynced: true,
    };
  }

  return {
    source: 'manual',
    sourceId: null,
    syncedAt: null,
    isSynced: false,
  };
}

// ----------------------------------------------------------------------

interface Props {
  row?: any;
  source?: string | null;
  sourceId?: string | null;
  syncedAt?: string | null;
  size?: 'small' | 'medium';
}

export function EntitySourceBadge({
  row,
  source: explicitSource,
  sourceId: explicitSourceId,
  syncedAt: explicitSyncedAt,
  size = 'small',
}: Props) {
  const sourceInfo: EntitySourceInfo = row
    ? getEntitySourceInfo(row)
    : {
        source: explicitSource ? explicitSource.toLowerCase() : 'manual',
        sourceId: explicitSourceId ? String(explicitSourceId) : null,
        syncedAt: explicitSyncedAt ? String(explicitSyncedAt) : null,
        isSynced: Boolean(
          explicitSource &&
            explicitSource.toLowerCase() !== 'manual' &&
            explicitSource.toLowerCase() !== 'system'
        ),
      };

  const getSourceConfig = (src: string) => {
    switch (src) {
      case 'odoo':
        return {
          label: 'Odoo',
          icon: 'solar:refresh-circle-bold',
          sx: {
            bgcolor: 'rgba(113, 75, 103, 0.12)',
            color: '#714B67',
            borderColor: 'rgba(113, 75, 103, 0.35)',
            fontWeight: 700,
          },
        };
      case 'zoho':
        return {
          label: 'Zoho CRM',
          icon: 'solar:refresh-circle-bold',
          sx: {
            bgcolor: 'rgba(0, 102, 204, 0.12)',
            color: '#0066CC',
            borderColor: 'rgba(0, 102, 204, 0.35)',
            fontWeight: 700,
          },
        };
      case 'zendesk':
        return {
          label: 'Zendesk',
          icon: 'solar:refresh-circle-bold',
          sx: {
            bgcolor: 'rgba(3, 54, 61, 0.12)',
            color: '#03363D',
            borderColor: 'rgba(3, 54, 61, 0.35)',
            fontWeight: 700,
          },
        };
      case 'salla':
        return {
          label: 'Salla',
          icon: 'solar:shop-bold',
          sx: {
            bgcolor: 'rgba(0, 73, 86, 0.12)',
            color: '#004956',
            borderColor: 'rgba(0, 73, 86, 0.35)',
            fontWeight: 700,
          },
        };
      case 'manual':
      case 'system':
      default:
        if (src && src !== 'manual' && src !== 'system') {
          return {
            label: src.charAt(0).toUpperCase() + src.slice(1),
            icon: 'solar:link-bold',
            sx: {
              bgcolor: 'primary.lighter',
              color: 'primary.dark',
              borderColor: 'primary.light',
              fontWeight: 700,
            },
          };
        }
        return {
          label: 'Manual',
          icon: 'solar:user-bold',
          sx: {
            bgcolor: (theme: any) =>
              theme.palette.mode === 'dark' ? 'grey.800' : 'grey.100',
            color: 'text.secondary',
            borderColor: 'divider',
            fontWeight: 500,
          },
        };
    }
  };

  const config = getSourceConfig(sourceInfo.source);

  const tooltipContent = sourceInfo.isSynced ? (
    <Box sx={{ p: 0.5 }}>
      <Typography variant="subtitle2" sx={{ fontWeight: 700, fontSize: 12 }}>
        Synced from {config.label}
      </Typography>
      {sourceInfo.sourceId && (
        <Typography variant="caption" sx={{ display: 'block', fontSize: 11, mt: 0.25 }}>
          Record ID: <strong>{sourceInfo.sourceId}</strong>
        </Typography>
      )}
      {sourceInfo.syncedAt && (
        <Typography
          variant="caption"
          sx={{ display: 'block', fontSize: 10.5, color: 'text.secondary', mt: 0.25 }}
        >
          Synced at: {new Date(sourceInfo.syncedAt).toLocaleString()}
        </Typography>
      )}
    </Box>
  ) : (
    <Typography variant="caption" sx={{ fontSize: 11 }}>
      Created directly in Core360
    </Typography>
  );

  return (
    <Tooltip title={tooltipContent} arrow placement="top">
      <Chip
        size={size}
        variant="outlined"
        icon={<Iconify icon={config.icon} width={13} sx={{ color: 'inherit !important' }} />}
        label={config.label}
        sx={{
          height: size === 'small' ? 22 : 26,
          fontSize: size === 'small' ? 11 : 12,
          borderWidth: 1,
          ...config.sx,
        }}
      />
    </Tooltip>
  );
}
